// MUNYA OS · Onboarding tour custom · § 4 + § 11.ter · A2
//
// Componente liviano sin dependencias externas (react-joyride/driver.js añaden ~40KB).
// Highlight con overlay oscuro + recorte del target + tooltip arrow + nav teclado.

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { TOURS, type OnboardingTour } from '../lib/onboarding/tours.js';
import { useDismissable } from '../lib/use-dismissable.js';

interface OnboardingState {
  tours_completed: string[];
  tour_for_role: string | null;
  should_show: boolean;
  is_impersonating: boolean;
}

export function OnboardingTour(): ReactElement | null {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [tour, setTour] = useState<OnboardingTour | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    fetch('/api/v1/onboarding/state', { credentials: 'include', signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: OnboardingState | null) => {
        if (ac.signal.aborted || !data) return;
        setState(data);
        if (data.is_impersonating) return;
        if (data.should_show && data.tour_for_role && TOURS[data.tour_for_role]) {
          setTour(TOURS[data.tour_for_role] ?? null);
        }
      })
      .catch(() => {
        /* silent (incluye AbortError) */
      });
    return () => {
      ac.abort();
    };
  }, []);

  const close = useCallback(
    async (markComplete = false): Promise<void> => {
      if (markComplete && tour) {
        try {
          await fetch('/api/v1/onboarding/complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ tour_id: tour.id }),
          });
        } catch {
          // silent
        }
      }
      setTour(null);
      setStepIdx(0);
      setRect(null);
    },
    [tour],
  );

  // #298 #299 · silenciar para siempre: no reaparece en futuros accesos (reactivable en Preferencias).
  const dismissForever = useCallback(async (): Promise<void> => {
    try {
      await fetch('/api/v1/onboarding/dismiss', { method: 'POST', credentials: 'include' });
    } catch {
      // silent
    }
    setTour(null);
    setStepIdx(0);
    setRect(null);
  }, []);

  // Calcula bounding box del target cuando cambia el step
  useEffect(() => {
    if (!tour) return;
    const step = tour.steps[stepIdx];
    if (!step?.target) {
      setRect(null);
      return;
    }
    const target = step.target;
    if (!target) return;
    const findTarget = (): void => {
      const el = document.querySelector(target);
      if (el) {
        const r = el.getBoundingClientRect();
        // Targets del header desktop existen en DOM pero están display:none en <lg → tratarlos como sin target
        if (r.width === 0 || r.height === 0) {
          setRect(null);
          return;
        }
        setRect(r);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setRect(null);
      }
    };
    findTarget();
    const onResize = (): void => {
      findTarget();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [tour, stepIdx]);

  // Teclado: → / Enter para avanzar · ← para retroceder · ESC para saltar
  useEffect(() => {
    if (!tour) return;
    const activeTour = tour;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        void close(true);
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        if (stepIdx === activeTour.steps.length - 1) void close(true);
        else setStepIdx((s) => s + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setStepIdx((s) => Math.max(0, s - 1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [tour, stepIdx, close]);

  // · #456 · el fondo solo cierra con el gesto completo (mousedown + mouseup sobre el fondo)
  const { backdropProps } = useDismissable({
    onDismiss: () => {
      void close(true);
    },
  });

  if (!tour || !state) return null;
  const step = tour.steps[stepIdx];
  if (!step) return null;
  const isLast = stepIdx === tour.steps.length - 1;
  const isCenter = !step.target || step.placement === 'center' || !rect;

  // Tooltip position
  const tooltipStyle = isCenter
    ? ({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      } as const)
    : (() => {
        const r = rect;
        const placement = step.placement ?? 'bottom';
        const margin = 12;
        if (placement === 'top') {
          return {
            position: 'fixed',
            top: r.top - margin,
            left: Math.min(Math.max(r.left + r.width / 2, 168), window.innerWidth - 168),
            transform: 'translate(-50%, -100%)',
          } as const;
        }
        if (placement === 'left') {
          return {
            position: 'fixed',
            top: r.top + r.height / 2,
            left: r.left - margin,
            transform: 'translate(-100%, -50%)',
          } as const;
        }
        if (placement === 'right') {
          return {
            position: 'fixed',
            top: r.top + r.height / 2,
            left: r.right + margin,
            transform: 'translate(0, -50%)',
          } as const;
        }
        return {
          position: 'fixed',
          top: r.bottom + margin,
          left: Math.min(Math.max(r.left + r.width / 2, 168), window.innerWidth - 168),
          transform: 'translate(-50%, 0)',
        } as const;
      })();

  return (
    <div
      role="dialog"
      aria-label="Tour de bienvenida"
      aria-modal="false"
      className="fixed inset-0 z-[100] pointer-events-none"
    >
      {/* Overlay con recorte del target */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto">
        <defs>
          <mask id="onboarding-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 4}
                y={rect.top - 4}
                width={rect.width + 8}
                height={rect.height + 8}
                rx="6"
                ry="6"
                fill="black"
              />
            )}
          </mask>
        </defs>
        {/* ⚠️ Los props del fondo van AQUÍ, no en el <svg>. En SVG el puntero lo recibe la
            figura pintada, así que con la regla «target === currentTarget» el contenedor no
            se enteraba nunca y el tour dejaba de cerrarse al tocar la zona oscurecida. */}
        <rect
          width="100%"
          height="100%"
          fill="rgba(54, 46, 44, 0.55)"
          mask="url(#onboarding-mask)"
          {...backdropProps}
        />
        {rect && (
          <rect
            x={rect.left - 4}
            y={rect.top - 4}
            width={rect.width + 8}
            height={rect.height + 8}
            rx="6"
            ry="6"
            fill="none"
            stroke="var(--color-munya-olive)"
            strokeWidth="2"
            className="pointer-events-none"
          />
        )}
      </svg>

      {/* Tooltip */}
      <div
        style={tooltipStyle}
        className="pointer-events-auto max-w-[calc(100vw-2rem)] bg-[var(--color-munya-surface)] rounded-lg shadow-2xl border border-[var(--color-munya-sand)] p-5 w-80"
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-[10px] font-medium uppercase tracking-wide opacity-60">
            Paso {stepIdx + 1} de {tour.steps.length}
          </span>
          <button
            type="button"
            onClick={() => void close(true)}
            aria-label="Saltar tour"
            className="opacity-60 hover:opacity-100"
          >
            <X size={14} />
          </button>
        </div>
        <h3 className="font-semibold text-base mb-1">{step.title}</h3>
        <p className="text-sm opacity-80 mb-4 leading-relaxed">{step.body}</p>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              setStepIdx((s) => Math.max(0, s - 1));
            }}
            disabled={stepIdx === 0}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-[var(--color-munya-sand)] disabled:opacity-30"
          >
            <CaretLeft size={12} /> Atrás
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void dismissForever()}
              className="text-xs opacity-50 hover:opacity-100"
              title="No volver a mostrar (reactivable en Preferencias)"
            >
              No volver a mostrar
            </button>
            <button
              type="button"
              onClick={() => void close(true)}
              className="text-xs opacity-60 hover:opacity-100"
            >
              Saltar
            </button>
            <button
              type="button"
              onClick={() => {
                if (isLast) void close(true);
                else setStepIdx((s) => s + 1);
              }}
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[var(--color-munya-olive)] text-white"
            >
              {isLast ? 'Listo' : 'Siguiente'} <CaretRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
